import React, { useState, useRef, useCallback } from 'react';
import { Bell, User, LogOut, Settings, Menu } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  type MasterAdminNotificationRow,
} from '../../../services/MasterAdmin/masterAdminNotificationService';
import {
  formatNotificationBody,
  getNotificationLink,
} from '../../../utils/masterAdminNotifications';
import AdminProfileEditModal from '../AdminProfileEditModal';

interface HeaderProps {
  title: string;
  /** Optional per-page headline (e.g. vendor avatar + name on ambulance details). */
  shellBranding?: { imageUrl?: string; primary: string } | null;
  onOpenMobileNav?: () => void;
  showNotifications?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  title,
  shellBranding = null,
  onOpenMobileNav,
  showNotifications = true,
}) => {
  const { user, logout, updateLocalUser } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotifMenu, setShowNotifMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const [notifications, setNotifications] = useState<MasterAdminNotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const [listRes, count] = await Promise.all([
        fetchNotifications({ page: 1, limit: 15 }),
        fetchUnreadCount(),
      ]);
      setNotifications(listRes.data.notifications);
      setUnreadCount(count);
    } catch {
      /* keep prior state on error */
    }
  }, []);

  React.useEffect(() => {
    if (!showNotifications) return;
    loadNotifications();
    const id = window.setInterval(loadNotifications, 45_000);
    return () => window.clearInterval(id);
  }, [loadNotifications, showNotifications]);

  React.useEffect(() => {
    if (!showNotifications || !showNotifMenu) return;
    setNotifLoading(true);
    fetchNotifications({ page: 1, limit: 15 })
      .then((res) => setNotifications(res.data.notifications))
      .finally(() => setNotifLoading(false));
    fetchUnreadCount().then(setUnreadCount);
  }, [showNotifMenu, showNotifications]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(target)) {
        setShowNotifMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const onClickNotification = async (n: MasterAdminNotificationRow) => {
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, isRead: true, readAt: new Date().toISOString() } : x))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    }
    const href = getNotificationLink(n);
    if (href) {
      setShowNotifMenu(false);
      navigate(href);
    }
  };

  const onMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      await loadNotifications();
    } catch {
      /* ignore */
    }
  };

  const onViewAllNotifications = () => {
    setShowNotifMenu(false);
    navigate(user?.role === 'WRITER' ? '/writer/notifications' : '/notifications');
  };

  return (
    <header className="shrink-0 border-b border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6 sm:py-4">
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => onOpenMobileNav?.()}
          className="inline-flex shrink-0 rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6" />
        </button>
        {shellBranding ? (
          <div className="min-w-0 flex-1 flex items-center gap-2 sm:gap-3">
            {shellBranding.imageUrl ? (
              <img
                src={shellBranding.imageUrl}
                alt=""
                className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-gray-200 dark:ring-gray-600 sm:h-10 sm:w-10"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900 sm:h-10 sm:w-10">
                <User className="h-5 w-5 text-primary-600 dark:text-primary-300" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-gray-800 dark:text-white sm:text-2xl">
                {shellBranding.primary}
              </p>
              {title ? (
                <p className="truncate text-xs text-gray-500 dark:text-gray-400 sm:text-sm">{title}</p>
              ) : null}
            </div>
          </div>
        ) : (
          <h1 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-800 dark:text-white sm:text-2xl">
            {title || 'Dashboard'}
          </h1>
        )}
        <div className="flex shrink-0 items-center gap-0.5 sm:gap-2 md:gap-3 lg:gap-4">
          <ThemeToggle />
          {showNotifications && (
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              aria-label="Notifications"
              className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
              onClick={() => setShowNotifMenu((v) => !v)}
            >
              <Bell className="w-6 h-6" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 inline-flex min-w-[1.25rem] items-center justify-center px-1 py-0.5 text-[10px] font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifMenu && (
              <div className="fixed inset-x-2 top-16 z-50 flex max-h-[min(70vh,28rem)] flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-none">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onViewAllNotifications}
                      className="rounded-full border border-primary-200 px-2.5 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-400 dark:hover:bg-primary-900/30"
                    >
                      View all
                    </button>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={onMarkAllRead}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifLoading && (
                    <div className="px-3 py-6 text-center text-sm text-gray-500">Loading…</div>
                  )}
                  {!notifLoading && notifications.length === 0 && (
                    <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No notifications yet.
                    </div>
                  )}
                  {!notifLoading &&
                    notifications.map((n) => {
                      const link = getNotificationLink(n);
                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => onClickNotification(n)}
                          className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/80 ${
                            !n.isRead ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                              {n.title}
                            </span>
                            {!n.isRead && (
                              <span className="shrink-0 w-2 h-2 mt-1.5 rounded-full bg-primary-500" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                            {formatNotificationBody(n)}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-gray-400 uppercase tracking-wide">{n.type}</span>
                            <span className="text-[10px] text-gray-400">
                              {new Date(n.createdAt).toLocaleString()}
                            </span>
                          </div>
                          {link && (
                            <span className="text-[10px] text-primary-600 dark:text-primary-400 mt-1 inline-block">
                              Open detail →
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
          )}
          <div className="relative" ref={userMenuRef}>
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              {user?.profile_image ? (
                <img
                  src={user.profile_image}
                  alt={user.name || 'Profile'}
                  className="w-8 h-8 rounded-full object-cover border border-primary-200 dark:border-primary-800"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary-600 dark:text-primary-300" />
                </div>
              )}
              <div className="hidden md:block">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name || 'Admin User'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email || 'admin@vedika.com'}
                </div>
              </div>
            </div>

            {showUserMenu && (
              <div className="fixed right-2 top-16 z-50 w-[min(12rem,calc(100vw-1rem))] rounded-md border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:right-0 sm:top-full sm:mt-2 sm:w-48">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setShowProfileModal(true);
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </button>
                <button
                  onClick={() => alert('Settings functionality coming soon!')}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <AdminProfileEditModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        createdByUserId={user?.id || ''}
        onSaved={(saved) => {
          updateLocalUser({
            name: String(saved.name || ''),
            email: String(saved.email || ''),
            department: String(saved.department || ''),
            phone: String(saved.phone || ''),
            profile_image: String(saved.profile_image || ''),
            role: (saved.role as any) || user?.role,
          });
        }}
      />
    </header>
  );
};

export default Header;
